/**
 * Responsibility: put one card into sub-collections, and take it out again.
 *
 * Chips that toggle rather than a multi-select, because the question being
 * answered is "is this card in that grouping" one grouping at a time, and a
 * multi-select hides the answer behind a click.
 *
 * Each toggle sends the card's **whole** set of groupings, not a difference.
 * Adding and removing are then the same call, and there is no second path
 * that could disagree about the result.
 */
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { CollectionItem, CollectionTag } from '../../api/client';
import { useSetHoldingTags } from '../../api/hooks/useTags';

type Props = {
  item: CollectionItem;
  tags: CollectionTag[];
};

export function HoldingTagPicker({ item, tags }: Props) {
  const setTags = useSetHoldingTags();
  const current = new Set(item.tags ?? []);

  if (tags.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No sub-collections yet. Make one from the collection toolbar to group cards
        by a run you are completing, or by anything else.
      </Typography>
    );
  }

  const toggle = (tagId: string) => {
    const next = new Set(current);
    if (next.has(tagId)) next.delete(tagId);
    else next.add(tagId);
    setTags.mutate({ itemId: item.id, tags: [...next] });
  };

  return (
    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        Sub-collections
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {tags.map((tag) => {
          const inIt = current.has(tag.id);
          return (
            <Chip
              key={tag.id}
              size="small"
              label={tag.name}
              color={inIt ? 'primary' : 'default'}
              variant={inIt ? 'filled' : 'outlined'}
              onClick={() => toggle(tag.id)}
              disabled={setTags.isPending}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}
